import React, { useContext, useEffect, useMemo, useState } from "react";
import moment from "moment";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  Grid,
  Hidden,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  AccessTime,
  AssignmentTurnedIn,
  BarChart,
  CheckCircle,
  ErrorOutline,
  FilterList,
  Forum,
  Group,
  HourglassEmpty,
  Inbox,
  LocalOffer,
  MailOutline,
  Message,
  PeopleAlt,
  Refresh,
  Schedule,
  Send,
  Star,
  TrendingDown,
  TrendingUp,
  Warning,
} from "@material-ui/icons";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import ForbiddenPage from "../../components/ForbiddenPage";
import { AuthContext } from "../../context/Auth/AuthContext";
import useDashboard from "../../hooks/useDashboard";

const useStyles = makeStyles((theme) => ({
  page: {
    minHeight: "100%",
    background: "#f6f8fb",
    overflowY: "auto",
    marginLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    boxSizing: "border-box",
    borderTopLeftRadius: 18,
    [theme.breakpoints.down("sm")]: {
      marginLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
      borderTopLeftRadius: 12,
    },
    [theme.breakpoints.down("xs")]: {
      marginLeft: 0,
      paddingRight: 0,
      borderTopLeftRadius: 0,
    },
  },
  container: {
    paddingTop: theme.spacing(3),
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(5),
    paddingBottom: theme.spacing(4),
    [theme.breakpoints.down("sm")]: {
      paddingLeft: theme.spacing(2.5),
      paddingRight: theme.spacing(3),
    },
    [theme.breakpoints.down("xs")]: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },
  title: {
    fontSize: 30,
    fontWeight: 800,
    color: "#111827",
    letterSpacing: 0,
    [theme.breakpoints.down("xs")]: {
      fontSize: 24,
    },
  },
  subtitle: {
    color: "#667085",
    marginTop: theme.spacing(0.5),
  },
  filterBar: {
    border: "1px solid #e5e7eb",
    borderRadius: 16,
    padding: theme.spacing(2),
    background: "#ffffff",
    boxShadow: "0 12px 28px rgba(16, 24, 40, 0.05)",
    marginBottom: theme.spacing(3),
  },
  field: {
    minWidth: 180,
    width: "100%",
  },
  refreshButton: {
    height: 40,
    borderRadius: 10,
    textTransform: "none",
    fontWeight: 700,
    boxShadow: "0 8px 16px rgba(25, 118, 210, 0.18)",
  },
  sectionCard: {
    height: "100%",
    border: "1px solid #e5e7eb",
    borderRadius: 18,
    background: "#ffffff",
    boxShadow: "0 12px 28px rgba(16, 24, 40, 0.05)",
    overflow: "hidden",
  },
  sectionInner: {
    padding: theme.spacing(2.5),
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(2),
    },
  },
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    fontSize: 17,
    fontWeight: 800,
    color: "#111827",
  },
  sectionCaption: {
    color: "#667085",
    fontSize: 13,
    marginTop: 2,
  },
  kpiCard: {
    height: "100%",
    borderRadius: 18,
    border: "1px solid #e5e7eb",
    background: "#ffffff",
    boxShadow: "0 12px 28px rgba(16, 24, 40, 0.05)",
    padding: theme.spacing(2.25),
  },
  kpiTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: theme.spacing(1.5),
  },
  iconWrap: {
    width: 44,
    height: 44,
    borderRadius: 14,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  kpiValue: {
    fontSize: 30,
    lineHeight: 1.1,
    fontWeight: 850,
    color: "#101828",
    marginTop: theme.spacing(2),
  },
  kpiLabel: {
    color: "#667085",
    fontSize: 13,
    fontWeight: 600,
    marginTop: theme.spacing(0.5),
  },
  trend: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    borderRadius: 999,
    padding: "4px 8px",
    fontSize: 12,
    fontWeight: 800,
  },
  alertItem: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    borderRadius: 14,
    border: "1px solid #e5e7eb",
    background: "#f9fafb",
    marginBottom: theme.spacing(1),
  },
  alertIcon: {
    width: 34,
    height: 34,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  emptyState: {
    minHeight: 150,
    borderRadius: 14,
    border: "1px dashed #cbd5e1",
    background: "#f8fafc",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    padding: theme.spacing(3),
  },
  tableContainer: {
    borderRadius: 14,
    border: "1px solid #e5e7eb",
    boxShadow: "none",
  },
  tableHeadCell: {
    background: "#f8fafc",
    color: "#475467",
    fontSize: 12,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: 0,
    whiteSpace: "nowrap",
  },
  tableCell: {
    color: "#344054",
    borderBottom: "1px solid #eef2f7",
    whiteSpace: "nowrap",
  },
  userCell: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.25),
    minWidth: 190,
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    display: "inline-block",
    marginRight: 6,
  },
  rankingItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.25, 0),
    borderBottom: "1px solid #eef2f7",
  },
  rankBadge: {
    width: 28,
    height: 28,
    borderRadius: 10,
    background: "#eff6ff",
    color: "#1565c0",
    fontWeight: 900,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    flexShrink: 0,
  },
  satisfactionBar: {
    display: "flex",
    height: 14,
    overflow: "hidden",
    borderRadius: 999,
    background: "#eef2f7",
    margin: theme.spacing(2, 0),
  },
  segment: {
    height: "100%",
  },
  queueRow: {
    padding: theme.spacing(1.5, 0),
    borderBottom: "1px solid #eef2f7",
  },
  queueHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
  },
  queueName: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    minWidth: 0,
  },
  colorDot: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    flexShrink: 0,
  },
  metricGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.5),
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    },
  },
  miniMetric: {
    borderRadius: 12,
    background: "#f8fafc",
    padding: theme.spacing(1),
  },
  miniLabel: {
    color: "#667085",
    fontSize: 11,
    fontWeight: 700,
  },
  miniValue: {
    color: "#111827",
    fontSize: 16,
    fontWeight: 850,
    marginTop: 2,
  },
  messageStat: {
    borderRadius: 14,
    background: "#f8fafc",
    padding: theme.spacing(1.5),
    height: "100%",
  },
  loadingLayer: {
    position: "absolute",
    inset: 0,
    background: "rgba(255, 255, 255, 0.68)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2,
  },
  relative: {
    position: "relative",
  },
}));

const statusColors = {
  info: { main: "#1976d2", soft: "#e8f2ff", text: "#155eef" },
  success: { main: "#12b76a", soft: "#ecfdf3", text: "#027a48" },
  warning: { main: "#f79009", soft: "#fffaeb", text: "#b54708" },
  danger: { main: "#f04438", soft: "#fef3f2", text: "#b42318" },
  neutral: { main: "#667085", soft: "#f2f4f7", text: "#475467" },
};

const periodOptions = [
  { value: "today", label: "Hoje" },
  { value: "yesterday", label: "Ontem" },
  { value: "7", label: "Últimos 7 dias" },
  { value: "30", label: "Últimos 30 dias" },
  { value: "custom", label: "Período personalizado" },
];

const formatNumber = (value) => Number(value || 0).toLocaleString("pt-BR");

const formatPercent = (value) =>
  `${Number(value || 0).toLocaleString("pt-BR", {
    maximumFractionDigits: 1,
  })}%`;

const formatTime = (minutes) => {
  const total = Math.max(0, Math.round(Number(minutes || 0)));
  const hours = Math.floor(total / 60);
  const mins = total % 60;

  if (hours <= 0) return `${mins}min`;
  return `${hours}h ${String(mins).padStart(2, "0")}min`;
};

const getDateRange = (period, customStart, customEnd) => {
  if (period === "today") {
    return {
      date_from: moment().format("YYYY-MM-DD"),
      date_to: moment().format("YYYY-MM-DD"),
    };
  }

  if (period === "yesterday") {
    return {
      date_from: moment().subtract(1, "day").format("YYYY-MM-DD"),
      date_to: moment().subtract(1, "day").format("YYYY-MM-DD"),
    };
  }

  if (period === "custom") {
    return {
      date_from: customStart,
      date_to: customEnd,
    };
  }

  return { days: Number(period) };
};

const SectionCard = ({ title, caption, action, children }) => {
  const classes = useStyles();

  return (
    <Paper className={classes.sectionCard} elevation={0}>
      <Box className={classes.sectionInner}>
        <Box className={classes.sectionHeader}>
          <Box>
            <Typography className={classes.sectionTitle}>{title}</Typography>
            {caption && (
              <Typography className={classes.sectionCaption}>{caption}</Typography>
            )}
          </Box>
          {action}
        </Box>
        {children}
      </Box>
    </Paper>
  );
};

const EmptyState = ({ icon, title, description }) => {
  const classes = useStyles();
  return (
    <Box className={classes.emptyState}>
      <Box>
        <Box mb={1}>{icon}</Box>
        <Typography style={{ fontWeight: 800, color: "#344054" }}>
          {title}
        </Typography>
        {description && (
          <Typography style={{ color: "#667085", fontSize: 13, marginTop: 4 }}>
            {description}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const KpiCard = ({ label, value, icon: Icon, tone, helper, trend }) => {
  const classes = useStyles();
  const colors = statusColors[tone] || statusColors.info;
  const trendTone = trend && trend.good ? statusColors.success : statusColors.warning;

  return (
    <Paper className={classes.kpiCard} elevation={0}>
      <Box className={classes.kpiTop}>
        <Box className={classes.iconWrap} style={{ background: colors.soft, color: colors.main }}>
          <Icon />
        </Box>
        {trend && (
          <Box className={classes.trend} style={{ background: trendTone.soft, color: trendTone.text }}>
            {trend.good ? <TrendingUp style={{ fontSize: 15 }} /> : <TrendingDown style={{ fontSize: 15 }} />}
            {trend.label}
          </Box>
        )}
      </Box>
      <Typography className={classes.kpiValue}>{value}</Typography>
      <Typography className={classes.kpiLabel}>{label}</Typography>
      {helper && (
        <Typography style={{ color: "#98a2b3", fontSize: 12, marginTop: 8 }}>
          {helper}
        </Typography>
      )}
    </Paper>
  );
};

const AttentionPoints = ({ alerts }) => {
  const classes = useStyles();

  if (!alerts.length) {
    return (
      <EmptyState
        icon={<CheckCircle style={{ color: statusColors.success.main, fontSize: 34 }} />}
        title="Tudo certo por enquanto."
        description="Nenhum ponto crítico identificado neste período."
      />
    );
  }

  return (
    <Box>
      {alerts.map((alert, index) => {
        const colors = statusColors[alert.tone] || statusColors.warning;
        return (
          <Box className={classes.alertItem} key={`${alert.title}-${index}`}>
            <Box className={classes.alertIcon} style={{ background: colors.soft, color: colors.main }}>
              {alert.icon}
            </Box>
            <Box>
              <Typography style={{ color: "#111827", fontWeight: 800, fontSize: 14 }}>
                {alert.title}
              </Typography>
              <Typography style={{ color: "#667085", fontSize: 13, marginTop: 2 }}>
                {alert.description}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

const TeamPerformance = ({ attendants }) => {
  const classes = useStyles();

  if (!attendants.length) {
    return (
      <EmptyState
        icon={<PeopleAlt style={{ color: statusColors.info.main, fontSize: 34 }} />}
        title="Nenhum atendente encontrado"
        description="Assim que sua equipe começar a atender, os indicadores aparecerão aqui."
      />
    );
  }

  const maxTickets = Math.max(...attendants.map((item) => Number(item.tickets || 0)), 0);
  const maxWait = Math.max(...attendants.map((item) => Number(item.avgWaitTime || 0)), 0);

  return (
    <TableContainer component={Paper} className={classes.tableContainer}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell className={classes.tableHeadCell}>Atendente</TableCell>
            <TableCell className={classes.tableHeadCell}>Status</TableCell>
            <TableCell align="center" className={classes.tableHeadCell}>Ativos</TableCell>
            <TableCell align="center" className={classes.tableHeadCell}>Finalizados</TableCell>
            <TableCell align="center" className={classes.tableHeadCell}>T. resposta</TableCell>
            <TableCell align="center" className={classes.tableHeadCell}>T. atendimento</TableCell>
            <TableCell align="center" className={classes.tableHeadCell}>Avaliação</TableCell>
            <TableCell align="center" className={classes.tableHeadCell}>Produtividade</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {attendants.map((attendant) => {
            const isBest = Number(attendant.tickets || 0) === maxTickets && maxTickets > 0;
            const isSlow = Number(attendant.avgWaitTime || 0) === maxWait && maxWait >= 10;
            const isIdle = Number(attendant.tickets || 0) === 0;
            const productivity = Math.min(100, Math.round((Number(attendant.tickets || 0) / Math.max(maxTickets, 1)) * 100));

            return (
              <TableRow key={attendant.id}>
                <TableCell className={classes.tableCell}>
                  <Box className={classes.userCell}>
                    <Avatar style={{ width: 34, height: 34, background: "#e8f2ff", color: "#155eef", fontSize: 14, fontWeight: 800 }}>
                      {String(attendant.name || "?").charAt(0).toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography style={{ color: "#111827", fontWeight: 800, fontSize: 14 }}>
                        {attendant.name}
                      </Typography>
                      <Box mt={0.5}>
                        {isBest && <Chip size="small" label="Melhor desempenho" style={{ background: "#ecfdf3", color: "#027a48", fontWeight: 700, marginRight: 4 }} />}
                        {isSlow && <Chip size="small" label="Maior espera" style={{ background: "#fffaeb", color: "#b54708", fontWeight: 700, marginRight: 4 }} />}
                        {isIdle && <Chip size="small" label="Sem atendimento" style={{ background: "#f2f4f7", color: "#475467", fontWeight: 700 }} />}
                      </Box>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell className={classes.tableCell}>
                  <span
                    className={classes.statusDot}
                    style={{ background: attendant.online ? statusColors.success.main : "#98a2b3" }}
                  />
                  {attendant.online ? "Online" : "Offline"}
                </TableCell>
                <TableCell align="center" className={classes.tableCell}>{formatNumber(attendant.activeTickets)}</TableCell>
                <TableCell align="center" className={classes.tableCell}>{formatNumber(attendant.finishedTickets || attendant.tickets)}</TableCell>
                <TableCell align="center" className={classes.tableCell}>{formatTime(attendant.avgWaitTime)}</TableCell>
                <TableCell align="center" className={classes.tableCell}>{formatTime(attendant.avgSupportTime)}</TableCell>
                <TableCell align="center" className={classes.tableCell}>{Number(attendant.rating || 0).toFixed(1)}</TableCell>
                <TableCell align="center" className={classes.tableCell}>
                  <Box display="flex" alignItems="center" style={{ gap: 8, minWidth: 120 }}>
                    <LinearProgress
                      variant="determinate"
                      value={productivity}
                      style={{ flex: 1, height: 8, borderRadius: 999, background: "#eef2f7" }}
                    />
                    <Typography style={{ fontSize: 12, fontWeight: 800, color: "#475467" }}>
                      {productivity}%
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const RankingList = ({ title, items, metric, empty }) => {
  const classes = useStyles();

  return (
    <Box mb={2.5}>
      <Typography style={{ color: "#344054", fontWeight: 850, fontSize: 14, marginBottom: 4 }}>
        {title}
      </Typography>
      {items.length === 0 ? (
        <Typography style={{ color: "#98a2b3", fontSize: 13 }}>{empty}</Typography>
      ) : (
        items.map((item, index) => (
          <Box className={classes.rankingItem} key={`${title}-${item.id}`}>
            <Box display="flex" alignItems="center" style={{ gap: 10, minWidth: 0 }}>
              <Box className={classes.rankBadge}>{index + 1}</Box>
              <Typography noWrap style={{ color: "#111827", fontWeight: 750 }}>
                {item.name}
              </Typography>
            </Box>
            <Typography style={{ color: "#475467", fontWeight: 850, fontSize: 13 }}>
              {metric(item)}
            </Typography>
          </Box>
        ))
      )}
    </Box>
  );
};

const Satisfaction = ({ counters }) => {
  const total = Number(counters.withRating || 0);
  const promoters = Number(counters.npsPromotersPerc || 0);
  const neutral = Number(counters.npsPassivePerc || 0);
  const detractors = Number(counters.npsDetractorsPerc || 0);
  const classes = useStyles();

  if (total === 0) {
    return (
      <EmptyState
        icon={<Star style={{ color: statusColors.warning.main, fontSize: 34 }} />}
        title="Ainda não há avaliações suficientes"
        description="As métricas de satisfação aparecerão assim que os clientes avaliarem os atendimentos."
      />
    );
  }

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <Box className={classes.messageStat}>
            <Typography className={classes.miniLabel}>Score geral</Typography>
            <Typography style={{ fontSize: 34, color: "#111827", fontWeight: 900 }}>
              {Math.round(Number(counters.npsScore || 0))}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={8}>
          <Box className={classes.satisfactionBar}>
            <Box className={classes.segment} style={{ width: `${promoters}%`, background: statusColors.success.main }} />
            <Box className={classes.segment} style={{ width: `${neutral}%`, background: statusColors.warning.main }} />
            <Box className={classes.segment} style={{ width: `${detractors}%`, background: statusColors.danger.main }} />
          </Box>
          <Grid container spacing={1}>
            <Grid item xs={4}>
              <Typography className={classes.miniLabel}>Promotores</Typography>
              <Typography className={classes.miniValue}>{formatPercent(promoters)}</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography className={classes.miniLabel}>Neutros</Typography>
              <Typography className={classes.miniValue}>{formatPercent(neutral)}</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography className={classes.miniLabel}>Detratores</Typography>
              <Typography className={classes.miniValue}>{formatPercent(detractors)}</Typography>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Divider style={{ margin: "18px 0" }} />
      <Grid container spacing={2}>
        <Grid item xs={6} sm={3}>
          <Typography className={classes.miniLabel}>Avaliações</Typography>
          <Typography className={classes.miniValue}>{formatNumber(counters.withRating)}</Typography>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Typography className={classes.miniLabel}>Aguardando</Typography>
          <Typography className={classes.miniValue}>{formatNumber(counters.waitRating)}</Typography>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Typography className={classes.miniLabel}>Sem avaliação</Typography>
          <Typography className={classes.miniValue}>{formatNumber(counters.withoutRating)}</Typography>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Typography className={classes.miniLabel}>Índice avaliado</Typography>
          <Typography className={classes.miniValue}>{formatPercent(counters.percRating)}</Typography>
        </Grid>
      </Grid>
    </Box>
  );
};

const QueueIndicators = ({ queues }) => {
  const classes = useStyles();

  if (!queues.length) {
    return (
      <EmptyState
        icon={<LocalOffer style={{ color: statusColors.info.main, fontSize: 34 }} />}
        title="Nenhuma fila com dados neste período"
        description="Os indicadores por fila aparecerão quando houver atendimentos vinculados aos setores."
      />
    );
  }

  return (
    <Box>
      {queues.map((queue) => (
        <Box className={classes.queueRow} key={queue.id}>
          <Box className={classes.queueHeader}>
            <Box className={classes.queueName}>
              <span className={classes.colorDot} style={{ background: queue.color || statusColors.info.main }} />
              <Typography noWrap style={{ color: "#111827", fontWeight: 850 }}>
                {queue.name}
              </Typography>
            </Box>
            <Chip
              size="small"
              label={`${formatNumber(Number(queue.openTickets || 0) + Number(queue.waitingTickets || 0))} ativos`}
              style={{ background: "#eff6ff", color: "#155eef", fontWeight: 800 }}
            />
          </Box>
          <Box className={classes.metricGrid}>
            <Box className={classes.miniMetric}>
              <Typography className={classes.miniLabel}>Abertos</Typography>
              <Typography className={classes.miniValue}>{formatNumber(queue.openTickets)}</Typography>
            </Box>
            <Box className={classes.miniMetric}>
              <Typography className={classes.miniLabel}>Aguardando</Typography>
              <Typography className={classes.miniValue}>{formatNumber(queue.waitingTickets)}</Typography>
            </Box>
            <Box className={classes.miniMetric}>
              <Typography className={classes.miniLabel}>Espera média</Typography>
              <Typography className={classes.miniValue}>{formatTime(queue.avgWaitTime)}</Typography>
            </Box>
            <Box className={classes.miniMetric}>
              <Typography className={classes.miniLabel}>Finalizados</Typography>
              <Typography className={classes.miniValue}>{formatNumber(queue.finishedTickets)}</Typography>
            </Box>
            <Box className={classes.miniMetric}>
              <Typography className={classes.miniLabel}>Atendentes</Typography>
              <Typography className={classes.miniValue}>{formatNumber(queue.attendants)}</Typography>
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const MessagesOverview = ({ counters }) => {
  const classes = useStyles();
  const received = Number(counters.messagesReceived || 0);
  const sent = Number(counters.messagesSent || 0);
  const maxMessages = Math.max(received, sent, 1);

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Box className={classes.messageStat}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography className={classes.miniLabel}>Recebidas</Typography>
              <Inbox style={{ color: statusColors.info.main }} />
            </Box>
            <Typography style={{ fontSize: 30, fontWeight: 900, color: "#111827" }}>
              {formatNumber(received)}
            </Typography>
            <LinearProgress variant="determinate" value={(received / maxMessages) * 100} style={{ height: 8, borderRadius: 999, marginTop: 10 }} />
          </Box>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Box className={classes.messageStat}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography className={classes.miniLabel}>Enviadas</Typography>
              <Send style={{ color: statusColors.success.main }} />
            </Box>
            <Typography style={{ fontSize: 30, fontWeight: 900, color: "#111827" }}>
              {formatNumber(sent)}
            </Typography>
            <LinearProgress variant="determinate" value={(sent / maxMessages) * 100} style={{ height: 8, borderRadius: 999, marginTop: 10 }} />
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Typography className={classes.miniLabel}>Média por atendimento</Typography>
          <Typography className={classes.miniValue}>{Number(counters.avgMessagesPerTicket || 0).toFixed(1)}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography className={classes.miniLabel}>Taxa de resposta</Typography>
          <Typography className={classes.miniValue}>{formatPercent(counters.responseRate)}</Typography>
        </Grid>
      </Grid>
    </Box>
  );
};

const Dashboard = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { user } = useContext(AuthContext);
  const { find } = useDashboard();

  const [counters, setCounters] = useState({});
  const [attendants, setAttendants] = useState([]);
  const [queues, setQueues] = useState([]);
  const [volume, setVolume] = useState([]);
  const [period, setPeriod] = useState("today");
  const [customStart, setCustomStart] = useState(moment().format("YYYY-MM-DD"));
  const [customEnd, setCustomEnd] = useState(moment().format("YYYY-MM-DD"));
  const [queueId, setQueueId] = useState("");
  const [userId, setUserId] = useState("");
  const [loading, setLoading] = useState(false);

  const queueOptions = useMemo(() => {
    const userQueues = Array.isArray(user?.queues) ? user.queues : [];
    const mappedUserQueues = userQueues.map((queue) => ({
      id: queue.id || queue.UserQueue?.queueId,
      name: queue.name,
    }));
    const dashboardQueues = queues.map((queue) => ({ id: queue.id, name: queue.name }));
    const merged = [...mappedUserQueues, ...dashboardQueues].filter((queue) => queue.id);

    return merged.filter(
      (queue, index, arr) => arr.findIndex((item) => Number(item.id) === Number(queue.id)) === index
    );
  }, [queues, user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        ...getDateRange(period, customStart, customEnd),
      };

      if (queueId) params.queueId = queueId;
      if (userId) params.userId = userId;

      const data = await find(params);
      setCounters(data.counters || {});
      setAttendants(data.attendants || []);
      setQueues(data.queues || []);
      setVolume(data.volume || []);
    } catch (error) {
      toast.error("Erro ao carregar dados do dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onlineUsers = attendants.filter((attendant) => attendant.online).length;
  const totalOpen = Number(counters.supportHappening || 0);
  const waiting = Number(counters.supportPending || 0);
  const finished = Number(counters.supportFinished || 0);
  const unanswered = Number(counters.unansweredTickets || 0);
  const avgWait = Number(counters.avgWaitTime || 0);
  const avgSupport = Number(counters.avgSupportTime || 0);

  const alerts = useMemo(() => {
    const items = [];
    const busiestQueue = [...queues].sort(
      (a, b) =>
        Number(b.openTickets || 0) + Number(b.waitingTickets || 0) -
        (Number(a.openTickets || 0) + Number(a.waitingTickets || 0))
    )[0];
    const overloaded = [...attendants].sort(
      (a, b) => Number(b.activeTickets || 0) - Number(a.activeTickets || 0)
    )[0];

    if (waiting > 0) {
      items.push({
        tone: avgWait >= 10 ? "danger" : "warning",
        icon: <HourglassEmpty />,
        title: `${formatNumber(waiting)} clientes aguardando`,
        description:
          avgWait >= 10
            ? `Tempo médio de espera em ${formatTime(avgWait)}. Priorize a fila de entrada.`
            : `Monitore a fila para evitar aumento do tempo médio de espera.`,
      });
    }

    if (unanswered > 0) {
      items.push({
        tone: unanswered >= 5 ? "danger" : "warning",
        icon: <MailOutline />,
        title: `${formatNumber(unanswered)} tickets sem resposta`,
        description: "Há atendimentos ativos com mensagens não lidas do cliente.",
      });
    }

    if (overloaded && Number(overloaded.activeTickets || 0) >= 8) {
      items.push({
        tone: "warning",
        icon: <PeopleAlt />,
        title: `${overloaded.name} pode estar sobrecarregado`,
        description: `${formatNumber(overloaded.activeTickets)} tickets ativos vinculados ao atendente.`,
      });
    }

    if (busiestQueue && Number(busiestQueue.openTickets || 0) + Number(busiestQueue.waitingTickets || 0) > 0) {
      items.push({
        tone: "info",
        icon: <LocalOffer />,
        title: `${busiestQueue.name} concentra a maior demanda`,
        description: `${formatNumber(Number(busiestQueue.openTickets || 0) + Number(busiestQueue.waitingTickets || 0))} tickets ativos ou aguardando.`,
      });
    }

    if (Number(counters.withRating || 0) === 0) {
      items.push({
        tone: "neutral",
        icon: <Star />,
        title: "Nenhuma avaliação registrada",
        description: "Ainda não há satisfação suficiente para leitura do período.",
      });
    }

    return items.slice(0, 5);
  }, [attendants, avgWait, counters, queues, unanswered, waiting]);

  const rankings = useMemo(() => {
    const withTickets = attendants.filter((attendant) => Number(attendant.tickets || 0) > 0);
    return {
      finished: [...withTickets]
        .sort((a, b) => Number(b.finishedTickets || b.tickets || 0) - Number(a.finishedTickets || a.tickets || 0))
        .slice(0, 3),
      rating: [...attendants]
        .filter((attendant) => Number(attendant.countRating || 0) > 0)
        .sort((a, b) => Number(b.rating || 0) - Number(a.rating || 0))
        .slice(0, 3),
      slow: [...attendants]
        .filter((attendant) => Number(attendant.avgWaitTime || 0) > 0)
        .sort((a, b) => Number(b.avgWaitTime || 0) - Number(a.avgWaitTime || 0))
        .slice(0, 3),
    };
  }, [attendants]);

  const summaryCards = [
    {
      label: "Atendimentos em aberto",
      value: formatNumber(totalOpen),
      icon: Forum,
      tone: totalOpen > 20 ? "warning" : "info",
      helper: "Tickets em atendimento agora",
    },
    {
      label: "Clientes aguardando",
      value: formatNumber(waiting),
      icon: HourglassEmpty,
      tone: waiting > 0 ? "warning" : "success",
      helper: "Tickets pendentes na fila",
    },
    {
      label: "Finalizados no período",
      value: formatNumber(finished),
      icon: AssignmentTurnedIn,
      tone: "success",
      helper: "Atendimentos concluídos",
      trend: finished > 0 ? { label: "ativo", good: true } : null,
    },
    {
      label: "Tempo médio de espera",
      value: formatTime(avgWait),
      icon: Schedule,
      tone: avgWait >= 10 ? "danger" : avgWait >= 5 ? "warning" : "success",
      helper: "Da entrada até o início",
    },
    {
      label: "Tempo médio de atendimento",
      value: formatTime(avgSupport),
      icon: AccessTime,
      tone: avgSupport >= 60 ? "warning" : "info",
      helper: "Do início ao fechamento",
    },
    {
      label: "Tickets sem resposta",
      value: formatNumber(unanswered),
      icon: ErrorOutline,
      tone: unanswered > 0 ? "danger" : "success",
      helper: "Tickets com mensagens não lidas",
    },
  ];

  if (user.profile === "user" && user.showDashboard === "disabled") {
    return <ForbiddenPage />;
  }

  return (
    <MainContainer>
      <Box className={classes.page}>
        <Container maxWidth={false} className={classes.container}>
          <Box className={classes.header}>
            <Box>
              <Typography className={classes.title}>Dashboard</Typography>
              <Typography className={classes.subtitle}>
                Visão geral dos atendimentos e desempenho da equipe
              </Typography>
            </Box>
            <Chip
              icon={<PeopleAlt />}
              label={`${onlineUsers}/${attendants.length} atendentes online`}
              style={{ background: "#ecfdf3", color: "#027a48", fontWeight: 800 }}
            />
          </Box>

          <Paper className={classes.filterBar} elevation={0}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={2}>
                <FormControl variant="outlined" size="small" className={classes.field}>
                  <InputLabel>Período</InputLabel>
                  <Select value={period} onChange={(event) => setPeriod(event.target.value)} label="Período">
                    {periodOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {period === "custom" && (
                <>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      type="date"
                      label="Data inicial"
                      value={customStart}
                      onChange={(event) => setCustomStart(event.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      type="date"
                      label="Data final"
                      value={customEnd}
                      onChange={(event) => setCustomEnd(event.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </>
              )}
              <Grid item xs={12} sm={6} md={2}>
                <FormControl variant="outlined" size="small" className={classes.field}>
                  <InputLabel>Fila/setor</InputLabel>
                  <Select value={queueId} onChange={(event) => setQueueId(event.target.value)} label="Fila/setor">
                    <MenuItem value="">Todas as filas</MenuItem>
                    {queueOptions.map((queue) => (
                      <MenuItem key={queue.id} value={queue.id}>{queue.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl variant="outlined" size="small" className={classes.field}>
                  <InputLabel>Atendente</InputLabel>
                  <Select value={userId} onChange={(event) => setUserId(event.target.value)} label="Atendente">
                    <MenuItem value="">Todos</MenuItem>
                    {attendants.map((attendant) => (
                      <MenuItem key={attendant.id} value={attendant.id}>{attendant.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md>
                <Button
                  fullWidth
                  color="primary"
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Refresh />}
                  className={classes.refreshButton}
                  onClick={fetchData}
                  disabled={loading}
                >
                  Atualizar dados
                </Button>
              </Grid>
              <Hidden mdUp>
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center" style={{ gap: 8, color: "#667085" }}>
                    <FilterList fontSize="small" />
                    <Typography variant="body2">Filtros aplicados ao dashboard inteiro</Typography>
                  </Box>
                </Grid>
              </Hidden>
            </Grid>
          </Paper>

          <Box className={classes.relative}>
            {loading && (
              <Box className={classes.loadingLayer}>
                <CircularProgress color="primary" />
              </Box>
            )}

            <Grid container spacing={2}>
              {summaryCards.map((card) => (
                <Grid item xs={12} sm={6} md={4} lg={2} key={card.label}>
                  <KpiCard {...card} />
                </Grid>
              ))}

              <Grid item xs={12} lg={8}>
                <SectionCard
                  title="Volume de atendimentos"
                  caption="Abertos, finalizados e aguardando no período selecionado"
                  action={<BarChart style={{ color: theme.palette.primary.main }} />}
                >
                  {volume.length === 0 ? (
                    <EmptyState
                      icon={<BarChart style={{ color: statusColors.info.main, fontSize: 34 }} />}
                      title="Nenhum atendimento encontrado neste período."
                      description="Assim que sua equipe começar a atender, os indicadores aparecerão aqui."
                    />
                  ) : (
                    <Box height={320}>
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={volume} margin={{ top: 8, right: 12, left: -18, bottom: 0 }}>
                          <CartesianGrid stroke="#eef2f7" vertical={false} />
                          <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fill: "#667085", fontSize: 12 }} />
                          <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={{ fill: "#667085", fontSize: 12 }} />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="opened" name="Abertos" fill="#1976d2" radius={[6, 6, 0, 0]} />
                          <Bar dataKey="waiting" name="Aguardando" fill="#f79009" radius={[6, 6, 0, 0]} />
                          <Line type="monotone" dataKey="finished" name="Finalizados" stroke="#12b76a" strokeWidth={3} dot={{ r: 3 }} />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </Box>
                  )}
                </SectionCard>
              </Grid>

              <Grid item xs={12} lg={4}>
                <SectionCard
                  title="Pontos de atenção"
                  caption="Situações que merecem ação do gestor"
                  action={<Warning style={{ color: alerts.length ? statusColors.warning.main : statusColors.success.main }} />}
                >
                  <AttentionPoints alerts={alerts} />
                </SectionCard>
              </Grid>

              <Grid item xs={12} lg={8}>
                <SectionCard
                  title="Performance da equipe"
                  caption="Status, produtividade, tempos médios e avaliações por atendente"
                >
                  <TeamPerformance attendants={attendants} />
                </SectionCard>
              </Grid>

              <Grid item xs={12} lg={4}>
                <SectionCard title="Ranking de atendentes" caption="Destaques rápidos para acompanhamento">
                  <RankingList
                    title="Top finalizações"
                    items={rankings.finished}
                    metric={(item) => `${formatNumber(item.finishedTickets || item.tickets)} atend.`}
                    empty="Sem finalizações no período."
                  />
                  <RankingList
                    title="Melhor avaliação"
                    items={rankings.rating}
                    metric={(item) => `${Number(item.rating || 0).toFixed(1)} pts`}
                    empty="Sem avaliações no período."
                  />
                  <RankingList
                    title="Maior tempo de resposta"
                    items={rankings.slow}
                    metric={(item) => formatTime(item.avgWaitTime)}
                    empty="Sem tempo de resposta registrado."
                  />
                </SectionCard>
              </Grid>

              <Grid item xs={12} lg={5}>
                <SectionCard title="Pesquisa de satisfação" caption="NPS, avaliações e cobertura do período">
                  <Satisfaction counters={counters} />
                </SectionCard>
              </Grid>

              <Grid item xs={12} lg={7}>
                <SectionCard title="Indicadores por fila/setor" caption="Demanda, espera média e capacidade por fila">
                  <QueueIndicators queues={queues} />
                </SectionCard>
              </Grid>

              <Grid item xs={12} md={5}>
                <SectionCard title="Mensagens recebidas e enviadas" caption="Volume operacional e taxa de resposta">
                  <MessagesOverview counters={counters} />
                </SectionCard>
              </Grid>

              <Grid item xs={12} md={7}>
                <SectionCard title="Resumo do período" caption="Leitura executiva dos principais sinais">
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Box className={classes.messageStat}>
                        <Group style={{ color: statusColors.info.main }} />
                        <Typography className={classes.miniLabel}>Novos contatos</Typography>
                        <Typography className={classes.miniValue}>{formatNumber(counters.leads)}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Box className={classes.messageStat}>
                        <Message style={{ color: statusColors.neutral.main }} />
                        <Typography className={classes.miniLabel}>Total de atendimentos</Typography>
                        <Typography className={classes.miniValue}>{formatNumber(counters.tickets)}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Box className={classes.messageStat}>
                        <CheckCircle style={{ color: statusColors.success.main }} />
                        <Typography className={classes.miniLabel}>Demandas passivas</Typography>
                        <Typography className={classes.miniValue}>{formatNumber(counters.passiveTickets)}</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </SectionCard>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>
    </MainContainer>
  );
};

export default Dashboard;
